/**
 * Shared message-log UI — used by both source-detail (account-level) and
 * collection-detail (group/chat-level). Renders a list of messages with
 * skeleton loading, empty state, error state, and cursor-based load-more.
 */
import { FileText, Film, ImageIcon, MessageSquare, Mic, Paperclip } from 'lucide-react';
import { EmptyState } from '@/components/patterns/empty-state';
import { SectionCard } from '@/components/patterns/section-card';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { MessageMedia } from '@/lib/api';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function formatRelative(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'just now';
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface MessageItem {
  id: string;
  senderName: string;
  sentAt: string;
  text: string;
  hasMedia: boolean;
  /** Media annotations from the media:process sidecar; null for text messages. */
  media?: MessageMedia | null;
  /** Present on account-level messages; omitted on collection-level. */
  collectionTitle?: string;
}

// ---------------------------------------------------------------------------
// Media annotation
// ---------------------------------------------------------------------------

/**
 * Why a media message has no annotation. Keys are the `media.error` codes in
 * @brain/shared MEDIA_ERROR_CODES — kept as a local copy because apps/web does
 * not depend on @brain/*, so a code added there must be added here too or it
 * renders as the generic fallback. Anything unrecognized is an older row stamped
 * with a raw provider string, which is too noisy to show as-is.
 */
const MEDIA_ERROR_LABELS: Record<string, string> = {
  no_speech: 'No speech detected',
  no_bytes: 'Media could not be retrieved',
  too_large: 'Too large to process',
  cap_exceeded: 'Daily processing limit reached',
  provider_unavailable: 'Processing service unavailable',
  unsupported_type: 'This file type is not read yet',
  fetch_failed: 'Media could not be downloaded',
  unknown_error: 'Could not be processed',
};

function mediaErrorLabel(error: string): string {
  return MEDIA_ERROR_LABELS[error] ?? 'Could not be processed';
}

/**
 * Icon + noun per media kind. `document` and `video` reach here as well as the
 * two kinds we annotate: media:process parks them on `unsupported_type` rather
 * than skipping silently, and labelling everything non-audio "Image" made a PDF
 * read as an image stuck mid-analysis.
 */
const MEDIA_KINDS: Record<string, { Icon: typeof Mic; label: string; pending: string }> = {
  audio: { Icon: Mic, label: 'Voice note', pending: 'Transcribing…' },
  image: { Icon: ImageIcon, label: 'Image', pending: 'Analyzing…' },
  video: { Icon: Film, label: 'Video', pending: 'Processing…' },
  document: { Icon: FileText, label: 'Document', pending: 'Processing…' },
};

const UNKNOWN_KIND = { Icon: Paperclip, label: 'Attachment', pending: 'Processing…' } as const;

/**
 * The annotation panel under a media message — a voice-note transcript or an
 * image description, shown in full (not truncated) so it can actually be read
 * and eyeballed for quality. Unprocessed media shows a pending line instead,
 * which is how you tell "transcription is broken" from "transcription is slow".
 */
function MediaAnnotation({ media }: { media: MessageMedia }) {
  const { Icon, label, pending } = MEDIA_KINDS[media.type] ?? UNKNOWN_KIND;
  const body = media.type === 'audio' ? media.transcript : media.description;

  // Neither annotated nor failed ⇒ the sidecar has not reached it yet.
  if (!body && !media.error && !media.processed) {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-[12px] text-faint">
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
        {pending}
      </p>
    );
  }

  if (!body) {
    return (
      <p className="mt-1 flex items-center gap-1.5 text-[12px] text-faint">
        <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
        {label} — {media.error ? mediaErrorLabel(media.error) : 'nothing recorded'}
      </p>
    );
  }

  return (
    <div className="mt-1.5 rounded-md border-l-2 border-border bg-muted/40 py-1.5 pl-2.5 pr-3">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
        <span className="text-[11px] font-medium uppercase tracking-wide text-faint">{label}</span>
      </div>
      <p className="mt-1 whitespace-pre-wrap break-words text-[13px] leading-relaxed">{body}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageRow
// ---------------------------------------------------------------------------

const MEDIA_MARKER = '[media]';

export function MessageRow({ msg }: { msg: MessageItem }) {
  const media = msg.media ?? null;
  const annotation = media ? (media.transcript ?? media.description) : null;
  // With an annotation rendered below, the bare `[media]` marker is pure noise —
  // drop it so the transcript reads as the message body.
  const showText = !(annotation && msg.text === MEDIA_MARKER);

  return (
    <div className="flex items-start gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0">
      <div className="mt-0.5 h-7 w-7 shrink-0 rounded-full bg-muted" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="truncate text-[13.5px] font-semibold">{msg.senderName}</span>
          <span className="shrink-0 text-[12px] text-faint">{formatRelative(msg.sentAt)}</span>
        </div>
        {showText && (
          <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
            {msg.text || (msg.hasMedia ? MEDIA_MARKER : '—')}
          </p>
        )}
        {media && <MediaAnnotation media={media} />}
        {msg.collectionTitle && (
          <p className="mt-0.5 truncate text-[11.5px] text-faint">{msg.collectionTitle}</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageLog
// ---------------------------------------------------------------------------

export interface MessageLogProps {
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  messages: MessageItem[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

export function MessageLog({
  isLoading,
  isError,
  error,
  messages,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: MessageLogProps) {
  return (
    <SectionCard title="Recent messages">
      {isLoading ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          Failed to load messages — {(error as { message?: string })?.message ?? 'unknown error'}
        </p>
      ) : messages.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="No messages indexed yet"
          body="Messages will appear here once this chat starts receiving them."
        />
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-border-subtle">
            {messages.map((msg) => (
              <MessageRow key={msg.id} msg={msg} />
            ))}
          </div>

          {hasNextPage && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                disabled={isFetchingNextPage}
                onClick={onLoadMore}
                className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'min-w-[100px]')}
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}
